import { Point, Box } from '@awayjs/core';
import { GraphicsPathWinding } from '../draw/GraphicsPathWinding';
import { GraphicsPathCommand } from '../draw/GraphicsPathCommand';
import { GraphicsFillStyle } from '../draw/GraphicsFillStyle';
import { GraphicsStrokeStyle } from '../draw/GraphicsStrokeStyle';
import { GraphicsFactoryHelper } from '../draw/GraphicsFactoryHelper';
/**

 * Defines the values to use for specifying path-drawing commands.
 * The values in this class are used by the Graphics.drawPath() method,
 *or stored in the commands vector of a GraphicsPath object.
 */
var GraphicsPath = /** @class */ (function () {
    function GraphicsPath(commands, data, winding_rule) {
        if (commands === void 0) { commands = null; }
        if (data === void 0) { data = null; }
        if (winding_rule === void 0) { winding_rule = GraphicsPathWinding.EVEN_ODD; }
        this._orientedBoxBoundsDirty = true;
        this._lastDirtyID = 0;
        this._dirtyID = -1;
        this._startMerge = -1;
        this._endMerge = -1;
        this._mergeSource = -1;
        this._mergetarget = -1;
        this._connectedIdx = [];
        this._positionOffset = [];
        this.forceClose = false;
        this._cache = {};
        this._cacheSharedSegments = {};
        this._data = [];
        this._commands = [];
        this._style = null;
        this.verts = [];
        this._positions = [];
        if (commands != null && data != null) {
            this._data[0] = data;
            this._commands[0] = commands;
        }
        else {
            this._data[0] = [];
            this._commands[0] = [];
        }
        this._startPoint = new Point();
        this._cur_point = new Point();
        this._winding_rule = winding_rule;
        this._winding_directions = [];
    }
    Object.defineProperty(GraphicsPath.prototype, "dirty", {
        get: function () {
            return this._lastDirtyID !== this._dirtyID;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(GraphicsPath.prototype, "data_type", {
        get: function () {
            return GraphicsPath.data_type;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(GraphicsPath.prototype, "style", {
        get: function () {
            return this._style;
        },
        set: function (value) {
            this._style = value;
            this._dirtyID++;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(GraphicsPath.prototype, "fill", {
        get: function () {
            if (this._style == null)
                return null;
            if (this._style.data_type == GraphicsFillStyle.data_type)
                return this._style;
            return null;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(GraphicsPath.prototype, "stroke", {
        get: function () {
            if (this._style == null)
                return null;
            if (this._style.data_type == GraphicsStrokeStyle.data_type)
                return this._style;
            return null;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(GraphicsPath.prototype, "commands", {
        get: function () {
            return this._commands;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(GraphicsPath.prototype, "data", {
        get: function () {
            return this._data;
        },
        enumerable: true,
        configurable: true
    });
    GraphicsPath.prototype.cacheSegment = function (contourIdx, segmentIdx, type, startx, starty, endx, endy, ctrlx, ctrly) {
        if (ctrlx === void 0) { ctrlx = null; }
        if (ctrly === void 0) { ctrly = null; }
        var idStr = type.toString() + '#';
        idStr += startx > endx ? startx.toString() + '#' + endx.toString() : endx.toString() + '#' + startx.toString();
        idStr += starty > endy ? starty.toString() + '#' + endy.toString() : endy.toString() + '#' + starty.toString();
        if (ctrlx != null && ctrly != null)
            idStr += ctrlx.toString() + '#' + ctrly.toString();
        var curCacheItem = this._cache[idStr];
        if (curCacheItem == null) {
            this._cache[idStr] = curCacheItem = [];
        }
        curCacheItem.push(contourIdx);
        curCacheItem.push(segmentIdx);
        if (curCacheItem.length > 2) {
            this._cacheSharedSegments[idStr] = curCacheItem;
        }
    };
    GraphicsPath.prototype.curveTo = function (controlX, controlY, anchorX, anchorY) {
        // if controlpoint and anchor are same, we add lineTo command
        if (controlX == anchorX && controlY == anchorY) {
            this.lineTo(controlX, controlY);
            //this.moveTo(anchorX, anchorY);
            return;
        }
        // if anchor is current point, but controlpoint is different, we lineto controlpoint
        if (this._cur_point.x == anchorX &&
            this._cur_point.y == anchorY &&
            (this._cur_point.x != controlX || this._cur_point.y != controlY)) {
            this.lineTo(controlX, controlY);
            this.moveTo(anchorX, anchorY);
            return;
        }
        // if controlpoint is current point, but anchor is different, we lineto anchor
        if ((this._cur_point.x != anchorX || this._cur_point.y != anchorY) &&
            this._cur_point.x == controlX &&
            this._cur_point.y == controlY) {
            this.lineTo(anchorX, anchorY);
            return;
        }
        // if controlpoint and anchor are same as current point
        if (this._cur_point.x == anchorX &&
            this._cur_point.y == anchorY &&
            this._cur_point.x == controlX &&
            this._cur_point.y == controlY) {
            //console.log("curveTo command not added because startpoint and endpoint are the same.");
            this.lineTo(anchorX, anchorY);
            return;
        }
        if (this._commands[this._commands.length - 1].length == 0) {
            // every contour must start with a moveTo command, so we make sure we have correct startpoint
            this._commands[this._commands.length - 1].push(GraphicsPathCommand.MOVE_TO);
            this._data[this._data.length - 1].push(this._cur_point.x);
            this._data[this._data.length - 1].push(this._cur_point.y);
        }
        this._commands[this._commands.length - 1].push(GraphicsPathCommand.CURVE_TO);
        this._data[this._data.length - 1].push(controlX);
        this._data[this._data.length - 1].push(controlY);
        this._data[this._data.length - 1].push(anchorX);
        this._data[this._data.length - 1].push(anchorY);
        this._cur_point.x = anchorX;
        this._cur_point.y = anchorY;
        this._dirtyID++;
    };
    GraphicsPath.prototype.cubicCurveTo = function (controlX, controlY, control2X, control2Y, anchorX, anchorY) {
        console.log('cubicCurveTo not yet fully supported.');
        if (this._cur_point.x == anchorX && this._cur_point.y == anchorY) {
            //console.log("curveTo command not added because startpoint and endpoint are the same.");
            return;
        }
        if (this._commands[this._commands.length - 1].length == 0) {
            // every contour must start with a moveTo command, so we make sure we have correct startpoint
            this._commands[this._commands.length - 1].push(GraphicsPathCommand.MOVE_TO);
            this._data[this._data.length - 1].push(this._cur_point.x);
            this._data[this._data.length - 1].push(this._cur_point.y);
        }
        this._commands[this._commands.length - 1].push(GraphicsPathCommand.CURVE_TO);
        this._data[this._data.length - 1].push(controlX);
        this._data[this._data.length - 1].push(controlY);
        this._data[this._data.length - 1].push(anchorX);
        this._data[this._data.length - 1].push(anchorY);
        this._cur_point.x = anchorX;
        this._cur_point.y = anchorY;
        this._dirtyID++;
    };
    GraphicsPath.prototype.lineTo = function (x, y) {
        /*
        // We can't do this becouse morphes is failed. Should be filtered before tesselation

        if (this._cur_point.x == x && this._cur_point.y == y) {
            //console.log("lineTo command not added because startpoint and endpoint are the same.");
            return;
        }*/
        if (this._commands[this._commands.length - 1].length == 0) {
            // every contour must start with a moveTo command, so we make sure we have correct startpoint
            this._commands[this._commands.length - 1].push(GraphicsPathCommand.MOVE_TO);
            this._data[this._data.length - 1].push(this._cur_point.x);
            this._data[this._data.length - 1].push(this._cur_point.y);
        }
        this._commands[this._commands.length - 1].push(GraphicsPathCommand.LINE_TO);
        this._data[this._data.length - 1].push(x);
        this._data[this._data.length - 1].push(y);
        this._cur_point.x = x;
        this._cur_point.y = y;
        this._dirtyID++;
    };
    GraphicsPath.prototype.moveTo = function (x, y) {
        if (this._cur_point.x == x && this._cur_point.y == y) {
            //console.log("moveTo command not added because startpoint and endpoint are the same.");
            return;
        }
        // whenever a moveTo command apears, we start a new contour
        if (this._commands[this._commands.length - 1].length > 0) {
            this._commands.push([GraphicsPathCommand.MOVE_TO]);
            this._data.push([x, y]);
        }
        this._startPoint.x = x;
        this._startPoint.y = y;
        this._cur_point.x = x;
        this._cur_point.y = y;
        this._dirtyID++;
    };
    GraphicsPath.prototype.wideLineTo = function (x, y) { };
    GraphicsPath.prototype.wideMoveTo = function (x, y) { };
    /* merges 2 contours together that share segments. the shared segments get removed*/
    GraphicsPath.prototype.mergeContours = function () {
        //console.log("merge", this._mergeSource, this._mergetarget, this._startMerge, this._endMerge);
        //console.log("merge", this.data[this._mergetarget]);
        //console.log("merge", this.data[this._mergeSource]);
        var direction = -1;
        if (this._startMerge == this._endMerge) {
            // only 1 segment to process.
            // we need to look at the points to know in what direction the second path must be added to the first path
            var mergeIdx = this._connectedIdx[this._mergetarget][this._startMerge];
            var startx = this.data[this._mergetarget][this._positionOffset[this._mergetarget][this._startMerge] - 2];
            var starty = this.data[this._mergetarget][this._positionOffset[this._mergetarget][this._startMerge] - 1];
            var endx = this.data[this._mergetarget][this._positionOffset[this._mergetarget][this._startMerge]];
            var endy = this.data[this._mergetarget][this._positionOffset[this._mergetarget][this._startMerge] + 1];
            var startx2 = this.data[this._mergeSource][this._positionOffset[this._mergeSource][mergeIdx]];
            var starty2 = this.data[this._mergeSource][this._positionOffset[this._mergeSource][mergeIdx] + 1];
            var endx2 = this.data[this._mergeSource][this._positionOffset[this._mergeSource][mergeIdx] + 2];
            var endy2 = this.data[this._mergeSource][this._positionOffset[this._mergeSource][mergeIdx] + 3];
            if (startx == startx2 && starty == starty2 && endx == endx2 && endy == endy2) {
                direction = 1;
            }
        }
        var startMergeIdx = this._connectedIdx[this._mergetarget][this._startMerge];
        var endMergeIdx = this._connectedIdx[this._mergetarget][this._endMerge];
        var numConnectedSegments = Math.abs(this._startMerge - this._endMerge) + 1;
        var segmentsToProcess = this._commands[this._mergeSource].length - numConnectedSegments;
        var direction = -1;
        if (startMergeIdx > endMergeIdx) {
            direction = 1;
            //console.log("segment goes in opposite direction");
        }
        var cur = startMergeIdx;
        var end_x = 0;
        var end_y = 0;
        var prev_x = 0;
        var prev_y = 0;
        var ctrl_x = 0;
        var ctrl_y = 0;
        var curve_verts;
        var k = 0;
        var k_len = 0;
        var data_cnt = this._positionOffset[this._mergeSource][startMergeIdx];
        for (var i = 0; i < segmentsToProcess; i++) {
            cur += direction;
            if (cur >= this._commands[this._mergeSource].length) {
                cur -= this._commands[this._mergeSource].length;
            }
            if (cur < 0) {
                cur += this._commands[this._mergeSource].length;
            }
            switch (this._commands[this._mergeSource][cur]) {
                case GraphicsPathCommand.LINE_TO:
                    end_x = this.data[this._mergeSource][this._positionOffset[this._mergeSource][cur]];
                    end_y = this.data[this._mergeSource][this._positionOffset[this._mergeSource][cur] + 1];
                    //console.log("LINE_TO ", i, end_x, end_y);
                    this._positions[this._mergetarget].push(end_x);
                    this._positions[this._mergetarget].push(end_y);
                    prev_x = end_x;
                    prev_y = end_y;
                    break;
                case GraphicsPathCommand.CURVE_TO:
                    ctrl_x = this.data[this._mergeSource][this._positionOffset[this._mergeSource][cur]];
                    ctrl_y = this.data[this._mergeSource][this._positionOffset[this._mergeSource][cur] + 1];
                    end_x = this.data[this._mergeSource][this._positionOffset[this._mergeSource][cur] + 2];
                    end_y = this.data[this._mergeSource][this._positionOffset[this._mergeSource][cur] + 3];
                    //console.log("CURVE_TO ", i, ctrl_x, ctrl_y, end_x, end_y);
                    curve_verts = [];
                    GraphicsFactoryHelper.tesselateCurve(prev_x, prev_y, ctrl_x, ctrl_y, end_x, end_y, curve_verts);
                    k_len = curve_verts.length;
                    for (k = 0; k < k_len; k += 2) {
                        this._positions[this._mergetarget].push(curve_verts[k]);
                        this._positions[this._mergetarget].push(curve_verts[k + 1]);
                    }
                    prev_x = end_x;
                    prev_y = end_y;
                    break;
            }
        }
        this._startMerge = -1;
        this._endMerge = -1;
        this._mergetarget = -1;
        this._mergeSource = -1;
    };
    GraphicsPath.prototype.prepare = function () {
        // was not mutated internaly
        if (this._dirtyID === this._lastDirtyID) {
            return;
        }
        this._lastDirtyID = this._dirtyID;
        var closed;
        var commands;
        var isValidCommand = [];
        var contour_merged = [];
        var data;
        var c, i, k, k_len = 0;
        var posOffset, data_cnt = 0;
        var prev_x, prev_y, end_x, end_y, ctrl_x, ctrl_y = 0;
        var curve_verts;
        var myCacheItem;
        var cmd_len = this.commands.length;
        // commands may be empty
        if (cmd_len === 1 && !this.commands[0]) {
            return;
        }
        var eps = 1 / 100;
        for (var c_1 = 0; c_1 < cmd_len; c_1++) {
            var commands_1 = this.commands[c_1];
            var data_1 = this.data[c_1];
            this._positions[c_1] = [];
            this._connectedIdx[c_1] = [];
            this._positionOffset[c_1] = [];
            contour_merged[c_1] = false;
            isValidCommand[c_1] = [];
            // check if the path is closed.
            // if its not closed, we optionally close it by adding the extra lineTo-cmd
            closed = true;
            var gap = Math.abs(data_1[0] - data_1[data_1.length - 2]) + Math.abs(data_1[1] - data_1[data_1.length - 1]);
            if (gap > eps) {
                if (this.forceClose) {
                    commands_1[commands_1.length] = GraphicsPathCommand.LINE_TO;
                    data_1[data_1.length] = data_1[0];
                    data_1[data_1.length] = data_1[1];
                }
                else {
                    closed = false;
                }
            }
        }
        cmd_len = this.commands.length;
        for (var c_2 = 0; c_2 < cmd_len; c_2++) {
            var commands_2 = this.commands[c_2];
            var data_2 = this.data[c_2];
            data_cnt = 0;
            prev_x = data_2[data_cnt++];
            prev_y = data_2[data_cnt++];
            end_x = 0;
            end_y = 0;
            ctrl_x = 0;
            ctrl_y = 0;
            this._positions[c_2].push(prev_x);
            this._positions[c_2].push(prev_y);
            // now we collect the final position data
            // a command list is no longer needed for this position data,
            // we resolve all curves to line segments here
            this._startMerge = -1;
            this._endMerge = -1;
            this._mergetarget = -1;
            this._mergeSource = -1;
            data_cnt = 0;
            prev_x = data_2[data_cnt++];
            prev_y = data_2[data_cnt++];
            for (var i_1 = 1; i_1 < commands_2.length; i_1++) {
                switch (commands_2[i_1]) {
                    case GraphicsPathCommand.MOVE_TO:
                        console.log('ERROR ! ONLY THE FIRST COMMAND FOR A CONTOUR IS ALLOWED TO BE A \'MOVE_TO\' COMMAND');
                        break;
                    case GraphicsPathCommand.LINE_TO:
                        end_x = data_2[data_cnt++];
                        end_y = data_2[data_cnt++];
                        //console.log("LINE_TO ", i, end_x, end_y);
                        this._positions[c_2].push(end_x);
                        this._positions[c_2].push(end_y);
                        prev_x = end_x;
                        prev_y = end_y;
                        break;
                    case GraphicsPathCommand.CURVE_TO:
                        ctrl_x = data_2[data_cnt++];
                        ctrl_y = data_2[data_cnt++];
                        end_x = data_2[data_cnt++];
                        end_y = data_2[data_cnt++];
                        //console.log("CURVE_TO ", i, ctrl_x, ctrl_y, end_x, end_y);
                        curve_verts = [];
                        GraphicsFactoryHelper.tesselateCurve(prev_x, prev_y, ctrl_x, ctrl_y, end_x, end_y, curve_verts);
                        k_len = curve_verts.length;
                        for (k = 0; k < k_len; k += 2) {
                            this._positions[c_2].push(curve_verts[k]);
                            this._positions[c_2].push(curve_verts[k + 1]);
                        }
                        prev_x = end_x;
                        prev_y = end_y;
                        break;
                }
            }
        }
    };
    GraphicsPath.prototype.invalidate = function () {
        this._orientedBoxBoundsDirty = true;
        this._dirtyID++;
    };
    GraphicsPath.prototype.getBoxBounds = function (matrix3D, cache, target) {
        if (matrix3D === void 0) { matrix3D = null; }
        if (cache === void 0) { cache = null; }
        if (target === void 0) { target = null; }
        if (matrix3D)
            return this._internalGetBoxBounds(matrix3D, cache, target);
        if (this._orientedBoxBoundsDirty) {
            this._orientedBoxBoundsDirty = false;
            this._orientedBoxBounds = this._internalGetBoxBounds(null, this._orientedBoxBounds, null);
        }
        if (this._orientedBoxBounds != null)
            target = this._orientedBoxBounds.union(target, target || cache);
        return target;
    };
    GraphicsPath.prototype._internalGetBoxBounds = function (matrix3D, cache, target) {
        if (matrix3D === void 0) { matrix3D = null; }
        if (cache === void 0) { cache = null; }
        if (target === void 0) { target = null; }
        var minX = 0, minY = 0;
        var maxX = 0, maxY = 0;
        var len = this._positions.length;
        if (len == 0)
            return target;
        var i = 0;
        var index = 0;
        var positions = this._positions[index++];
        var pLen = positions.length;
        var pos1, pos2, pos3, rawData;
        if (matrix3D)
            rawData = matrix3D._rawData;
        if (target == null) {
            target = cache || new Box();
            if (matrix3D) {
                pos1 = positions[i] * rawData[0] + positions[i + 1] * rawData[4] + rawData[12];
                pos2 = positions[i] * rawData[1] + positions[i + 1] * rawData[5] + rawData[13];
            }
            else {
                pos1 = positions[i];
                pos2 = positions[i + 1];
            }
            maxX = minX = pos1;
            maxY = minY = pos2;
            i += 2;
        }
        else {
            maxX = (minX = target.x) + target.width;
            maxY = (minY = target.y) + target.height;
        }
        for (; i < pLen; i += 2) {
            if (matrix3D) {
                pos1 = positions[i] * rawData[0] + positions[i + 1] * rawData[4] + rawData[12];
                pos2 = positions[i] * rawData[1] + positions[i + 1] * rawData[5] + rawData[13];
            }
            else {
                pos1 = positions[i];
                pos2 = positions[i + 1];
            }
            if (pos1 < minX)
                minX = pos1;
            else if (pos1 > maxX)
                maxX = pos1;
            if (pos2 < minY)
                minY = pos2;
            else if (pos2 > maxY)
                maxY = pos2;
            if (i >= pLen - 2 && index < len) {
                i = 0;
                positions = this._positions[index++];
                pLen = positions.length;
            }
        }
        target.width = maxX - (target.x = minX);
        target.height = maxY - (target.y = minY);
        target.depth = 0;
        return target;
    };
    GraphicsPath.data_type = '[graphicsdata path]';
    return GraphicsPath;
}());
export { GraphicsPath };
